import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren
} from '@angular/core';

import { TreeQuestionResponse } from '../../models';

/**
 * Based on this example:
 * https://w3c.github.io/aria-practices/examples/listbox/listbox-scrollable.html
 */
@Component({
  selector: 'aa-response-list',
  templateUrl: './response-list.component.html',
  styleUrls: ['./response-list.component.scss']
})
export class ResponseListComponent implements AfterViewInit {

  @ViewChild('listBox') listBoxElement: ElementRef;
  @ViewChildren('listItem') listItems: QueryList<ElementRef>;

  @Input() labelledBy: string;
  @Input() responses: TreeQuestionResponse[] = [];
  @Input() focus: boolean = true;

  private activeItem: ElementRef;

  constructor(private readonly renderer: Renderer2) { }

  ngAfterViewInit() {
    if (this.focus) {
      this.listBoxElement.nativeElement.focus();
    }
  }

  @HostListener('keydown', ['$event']) handleKeyPress(evt: KeyboardEvent) {

    switch (evt.key) {
      case 'Down': // IE/Edge specific value
      case 'ArrowDown':
        this.focusItem(this.getRelativeItem(+1));
        break;

      case 'Up': // IE/Edge specific value
      case 'ArrowUp':
        this.focusItem(this.getRelativeItem(-1));
        break;

      case 'Home':
        this.focusFirstItem();
        break;

      case 'End':
        this.focusLastItem();
        break;

      case 'Enter':
        this.clickActiveItem();
        break;

      default:
        return;
    }

    evt.preventDefault();
  }

  private clickActiveItem() {
    if (!this.activeItem) {
      return;
    }

    this.activeItem.nativeElement.click();
  }

  private getRelativeItem(offset: number): ElementRef {
    if (!this.activeItem) {
      return null;
    }

    const items = this.listItems.toArray();
    const refIndex = items.indexOf(this.activeItem);

    if (refIndex < 0) {
      return null;
    }

    const index = refIndex + offset;

    if (index < 0 || index >= items.length) {
      return null;
    }

    return items[index];
  }

  public setupFocus() {
    if (this.activeItem) {
      return;
    }

    this.focusFirstItem();
  }

  private focusFirstItem() {
      this.focusItem(this.listItems.first);
  }

  private focusLastItem() {
    this.focusItem(this.listItems.last);
  }

  private focusItem (element: ElementRef) {
    if (!element) {
      return;
    }

    this.defocusItem(this.activeItem);

    const el = element.nativeElement;
    this.activeItem = element;

    this.renderer.setAttribute(el, 'aria-selected', 'true');
    this.renderer.addClass(el, 'focused');
    this.renderer.setAttribute(this.listBoxElement.nativeElement, 'aria-activedescendant', el.id);
  }

  private defocusItem(element: ElementRef) {
    if (!element) {
      return;
    }
    const el = element.nativeElement;
    this.renderer.removeAttribute(el, 'aria-selected');
    this.renderer.removeClass(el, 'focused');
  }
}
